import {
    AddOutlined,
    CloseOutlined,
    DeleteOutlined,
    EditOutlined,
    ReceiptOutlined,
} from "@mui/icons-material";
import {
    Avatar,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import useActionDialog from "../../components/ActionDialog";
import { useSideSheet } from "../../components/SideSheet";
import { isEmpty, set } from "lodash";
import { FormFieldErrors, hasErrors, useForm, validateMandatoryField } from "../../utils/form";
import { useCallback } from "react";

const PaymentInstructionsDialog = ({ open, hide, onChange, data, disabled, idNumberLabel }) => {
    const showDialog = useActionDialog();
    const onSubmit = useCallback(
        (values) => {
            onChange?.(values);
            hide();
        },
        [hide, onChange]
    );
    const validate = useCallback((values, errors) => {
        validateMandatoryField(values.name, "name", errors);
        validateMandatoryField(values.type, "type", errors);

        //validate fields
        if (Array.isArray(values.fields)) {
            values.fields.forEach((field, index) => {
                ((field, errors, index) => {
                    validateMandatoryField(field.label, `fields.${index}.label`, errors);
                    validateMandatoryField(field.source, `fields.${index}.source`, errors);
                    validateMandatoryField(field.value, `fields.${index}.value`, errors);
                })(field, errors, index);
            });
        }
    }, []);
    const { values, handleChange, handleSubmit, setValues, errors } = useForm({
        data,
        onSubmit,
        validate,
    });

    return (
        <Dialog fullWidth maxWidth="md" open={open} onClose={hide}>
            <DialogTitle>Platební instrukce</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={hide}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}>
                <CloseOutlined />
            </IconButton>
            <DialogContent>
                <Box mb={2}>
                    <TextField
                        label="Název"
                        name="name"
                        value={values.name || ""}
                        onChange={handleChange}
                        required
                        margin="normal"
                        fullWidth
                        error={hasErrors(errors, "name")}
                        helperText={<FormFieldErrors errors={errors} field="name" />}
                    />
                    <FormHelperText>
                        Název slouží jen pro identifikaci platebních instrukcí v rámci systému
                    </FormHelperText>
                    <TextField
                        label="Typ"
                        name="type"
                        value={values.type || ""}
                        onChange={handleChange}
                        required
                        margin="normal"
                        fullWidth
                        error={hasErrors(errors, "type")}
                        helperText={<FormFieldErrors errors={errors} field="type" />}
                    />
                </Box>
                <Box>
                    <Typography variant="h6">Datová pole</Typography>
                    <List>
                        {(values.fields || []).map((field, index) => (
                            <PaymentField
                                key={index}
                                index={index}
                                disabled={disabled}
                                errors={errors}
                                fieldPath={"fields"}
                                idNumberLabel={idNumberLabel}
                                value={field}
                                onChange={(value) => {
                                    setValues({
                                        ...values,
                                        fields: set(values.fields, index, value),
                                    });
                                }}
                                onDelete={() =>
                                    showDialog(
                                        "Smazat datové pole",
                                        isEmpty(field.label)
                                            ? "Chcete odstranit datové pole bez názvu?"
                                            : `Chcete odstranit datové pole: ${field.label}?`,
                                        [
                                            { title: "Storno" },
                                            {
                                                title: "Smazat",
                                                variant: "contained",
                                                primary: true,
                                                onClick: () =>
                                                    setValues({
                                                        ...values,
                                                        fields: values.fields.filter(
                                                            (_, i) => i !== index
                                                        ),
                                                    }),
                                            },
                                        ]
                                    )
                                }
                            />
                        ))}
                    </List>
                    <Button
                        onClick={() => {
                            setValues({ ...values, fields: [...(values.fields || []), {}] });
                        }}
                        disabled={disabled}
                        startIcon={<AddOutlined />}
                        size="small">
                        Přidat datové pole
                    </Button>
                </Box>
                {/*<Box mt={2}>
                    <TextField
                        margin="normal"
                        size="small"
                        label="Text"
                        name="description"
                        placeholder="Volitelný text pro popis platebních instrukcí"
                        value={values.description || ""}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={2}
                    />
                    </Box>*/}
            </DialogContent>
            <DialogActions sx={{ p: 2, px: 3 }}>
                <Button onClick={hide} disabled={disabled}>
                    Storno
                </Button>
                <Button variant="contained" disabled={disabled} onClick={handleSubmit}>
                    Uložit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const PaymentField = ({
    value,
    onChange,
    disabled,
    onDelete,
    fieldPath,
    index,
    errors,
    idNumberLabel,
}) => {
    const labelFieldPath = `${fieldPath}.${index}.label`;
    const sourceFieldPath = `${fieldPath}.${index}.source`;
    const valueFieldPath = `${fieldPath}.${index}.value`;

    return (
        <ListItem
            dense
            disableGutters
            secondaryAction={
                <IconButton edge="end" disabled={disabled} onClick={() => onDelete?.()}>
                    <DeleteOutlined />
                </IconButton>
            }>
            <Stack direction="row" gap={4} sx={{ flex: 1 }}>
                <Box sx={{ flex: 1 }}>
                    <TextField
                        margin="normal"
                        size="small"
                        label="Název"
                        name="label"
                        value={value.label || ""}
                        onChange={(e) => onChange?.({ ...value, label: e.target.value })}
                        error={hasErrors(errors, labelFieldPath)}
                        helperText={<FormFieldErrors errors={errors} field={labelFieldPath} />}
                        fullWidth
                    />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <FormControl
                        fullWidth
                        size="small"
                        margin="normal"
                        error={hasErrors(errors, sourceFieldPath)}
                        helperText={<FormFieldErrors errors={errors} field={sourceFieldPath} />}>
                        <InputLabel id={`source-${index}-id`}>Zdroj dat</InputLabel>
                        <Select
                            labelId={`source-${index}-id`}
                            value={value.source || ""}
                            label="Zdroj dat"
                            onChange={(e) =>
                                onChange?.({ ...value, source: e.target.value, value: "" })
                            }>
                            <MenuItem value="CUSTOM">Vlastní...</MenuItem>
                            <MenuItem value="TRANSACTION">Transakce</MenuItem>
                        </Select>
                        {hasErrors(errors, sourceFieldPath) && (
                            <FormFieldErrors errors={errors} field={sourceFieldPath} />
                        )}
                    </FormControl>
                </Box>
                <Box sx={{ flex: 2 }}>
                    {value.source === "TRANSACTION" ? (
                        <FormControl
                            fullWidth
                            size="small"
                            margin="normal"
                            error={hasErrors(errors, valueFieldPath)}>
                            <InputLabel id={`value-${index}-id`}>Hodnota</InputLabel>
                            <Select
                                labelId={`value-${index}-id`}
                                value={value.value || ""}
                                label="Hodnota"
                                onChange={(e) => onChange?.({ ...value, value: e.target.value })}>
                                <MenuItem value="idNumber">
                                    {idNumberLabel || "Identifikátor transakce"}
                                </MenuItem>
                            </Select>
                            {hasErrors(errors, valueFieldPath) && (
                                <FormFieldErrors errors={errors} field={valueFieldPath} />
                            )}
                        </FormControl>
                    ) : (
                        <TextField
                            margin="normal"
                            size="small"
                            label="Hodnota"
                            name="value"
                            value={value.value || ""}
                            onChange={(e) => onChange?.({ ...value, value: e.target.value })}
                            error={hasErrors(errors, valueFieldPath)}
                            helperText={<FormFieldErrors errors={errors} field={valueFieldPath} />}
                            fullWidth
                        />
                    )}
                </Box>
            </Stack>
        </ListItem>
    );
};

const PaymentInstructions = ({
    value,
    onChange,
    disabled,
    settingsMode,
    editable,
    idNumberLabel,
}) => {
    const showDialog = useActionDialog();
    const paymentInstructionsModal = useSideSheet();

    return (
        <>
            <List>
                {(value || []).map((item, index) => (
                    <ListItem
                        key={`${item.id}-${index}`}
                        dense
                        disableGutters
                        secondaryAction={
                            editable ? (
                                <Stack gap={1} direction="row">
                                    <IconButton
                                        edge="end"
                                        disabled={disabled}
                                        onClick={() =>
                                            paymentInstructionsModal.show({
                                                ...item,
                                                _idx: index,
                                            })
                                        }>
                                        <EditOutlined />
                                    </IconButton>
                                    <IconButton
                                        edge="end"
                                        disabled={disabled}
                                        onClick={() =>
                                            showDialog(
                                                "Smazat instrukce pro platbu",
                                                settingsMode
                                                    ? `Chcete odstranit platební instrukce: ${item.name}?`
                                                    : `Chcete odstranit platební instrukce ${item.name} z této transakce?`,
                                                [
                                                    { title: "Storno" },
                                                    {
                                                        title: "Smazat",
                                                        variant: "contained",
                                                        primary: true,
                                                        onClick: () => {
                                                            onChange?.(
                                                                value.filter((_, i) => i !== index)
                                                            );
                                                        },
                                                    },
                                                ],
                                                settingsMode
                                                    ? {
                                                          helperText:
                                                              "Na již vytvořených transakcích zůstanou instrukce pro platbu zachovány.",
                                                      }
                                                    : undefined
                                            )
                                        }>
                                        <DeleteOutlined />
                                    </IconButton>
                                </Stack>
                            ) : undefined
                        }>
                        <ListItemAvatar>
                            <Avatar>
                                <ReceiptOutlined />
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={item.name} secondary={item.type} />
                    </ListItem>
                ))}
            </List>
            {!!editable && (
                <Button
                    onClick={() => {
                        paymentInstructionsModal.show({});
                    }}
                    disabled={disabled}
                    startIcon={<AddOutlined />}
                    size="small">
                    Přidat platební instrukce
                </Button>
            )}
            <PaymentInstructionsDialog
                {...paymentInstructionsModal}
                idNumberLabel={idNumberLabel}
                onChange={({ _idx, ...data }) => {
                    onChange?.(
                        _idx === undefined
                            ? [...value, data]
                            : value.map((item, index) => (index === _idx ? data : item))
                    );
                }}
            />
        </>
    );
};

export default PaymentInstructions;
